import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { ConstantService } from 'src/app/services/common/constant.service';
import { DataTableDirective } from 'angular-datatables';
import { TranslateService } from '@ngx-translate/core';

/* 
  Notes for using this component

  1. Give nessecery settings by inputing a DataTableConfig
  2. tableOptions will overwrite the default options if provided
  3. Add 'className' and 'title' to tableColumns items for html display
  4. Use .bind(this) for row-click and button behaviour

*/

export class DataTableConfig {
  // Required
  tableID: string;
  loadTableData: (dataTablesParameters: any, callback: any) => void;
  tableColumns: any[];

  // Optional
  tableOptions?: any;
  tableButtons?: any[] = [];
  tableRowCallback?: any[] = [];
  createdRow?: (row: any, data: any, index: any) => void;

  constructor(datatableConfig?: Partial<DataTableConfig>) {
    Object.assign(this, datatableConfig);
  }
}

@Component({
  standalone: false,
  selector: 'app-datatable',
  templateUrl: './datatable.component.html',
  styleUrls: ['./datatable.component.scss'],
})
export class DatatableComponent implements OnInit {
  private MaxRecords: 100000;
  @ViewChild(DataTableDirective, { static: false }) datatableElement: DataTableDirective;
  
  // TODO: add @Input({ required: true }) after angular 16
  @Input() config: DataTableConfig;
  @Input() showPageAll: boolean;

  private selectedRows: Set<string> = new Set();
  private deselectedRows: Set<string> = new Set();
  private lastPage: number = 0;
  private lastSearchText = '';
  private pageLength = 10;
  private data: any[] = [];
  public markAll = false;

  gridCheckAll: boolean = false;
  dtOptions: any = {};

  constructor(private constantService:ConstantService, private translate: TranslateService) { }

  ngOnInit(): void {
    this.initializeDataTable();
  }

  public getSelectedRows(){
    return Array.from(this.selectedRows).map(item => JSON.parse(item));
  }

  public getDeselectedRows(){
    return Array.from(this.deselectedRows).map(item => JSON.parse(item));

  }

  public reload(): void {
    this.selectedRows.clear();
    this.deselectedRows.clear();
    this.datatableElement.dtInstance.then((dtInstance: DataTables.Api) => {
      dtInstance.ajax.reload();
    });
  }

  private initializeDataTable() {
    const defaultOptions = {
      language: this.constantService.TableLang,
      info: true,
      pagingType: 'full_numbers',
      start: 0,
      length: 1,
      pageLength: this.pageLength,
      displayStart: this.lastPage,
      search: { search: this.lastSearchText },
      autoWidth: false,
      ordering: true,
      order: [[0, 'asc']],
      lengthMenu: [
        [5, 10, 20, 50],
        [5, 10, 20, 50]
      ],
      dom: 'lBfrtip',
      select: {},
      serverSide: true,
      processing: true,
      stateSave: true,
      stateSaveCallback: this.constantService.saveTableState,
      stateLoadCallback: this.constantService.loadTableState,
    };
    this.checkPaging(defaultOptions);
    if (this.showPageAll){
      defaultOptions.lengthMenu[0].push(1000000000)
      defaultOptions.lengthMenu[1].push(this.translate.instant('All'))
    }

    this.dtOptions = { ...defaultOptions, ...this.config.tableOptions};

    if(this.dtOptions.select.style && !this.dtOptions.order){
      this.dtOptions.order = [[1, 'asc']];
    }

    this.dtOptions.columns = this.dtOptions.select.style && !this.dtOptions.select.customFirstRow
      ? [
          {
            data: null,
            className: 'select-checkbox wd-5p',
            name: 'isSelect',
            targets: 0,
            defaultContent: '',
            orderable: false,
            sortable: false,
            searchable: false,
            render: () => '<span class="invisible"></span>',
          },
          ...this.config.tableColumns,
        ]
      : this.config.tableColumns;

    this.dtOptions.buttons = this.config.tableButtons.map(button => {
      return {
        text: button.text,
        attr: button.attr,
        action: (e, dt, node, config) => {
          button.action(e, dt, node, config);
        }
      };
    });

    this.dtOptions.ajax = (dataTablesParameters: any, callback) => {
      this.checkPaging(dataTablesParameters);
      this.data = dataTablesParameters.data;
      this.config.loadTableData(dataTablesParameters, callback);
    };

    this.dtOptions.rowCallback = (row: Node, data: any[] | Object, index: number) => {
      if(this.dtOptions.select.style){
        if(this.selectedRows.has(JSON.stringify(data)) && !this.deselectedRows.has(JSON.stringify(data))){
          $(row).addClass('selected');
        }else{
          $(row).removeClass('selected');
        }
      }
      
      this.config.tableRowCallback.forEach(action => {
        const element = $(action.selector, row);
        element.off('click');
        element.on('click', (event) => {
          event.stopPropagation(); // avoid other actions
          action.callback(data);
        });
      });

      if(this.dtOptions.select.style){
        const selector = this.dtOptions.select.selector || 'td';
        $(selector, row).off('click');
        $(selector, row).on('click', () => {
          this.updateSelectedRows(data);
        });
      }
      return row;
    };

    if(this.dtOptions.select.style){
      this.dtOptions.drawCallback = function (settings) {
        const api = this.api();
        api.rows().every(function (rowIdx, tableLoop, rowLoop) {
          if ($(api.row(rowIdx).node()).hasClass('selected')) {
            api.row(rowIdx).select();
          }
        });
      };
    }

    if(this.config.createdRow){
      this.dtOptions.createdRow = (row: any, data: any, index: any) => {
        this.config.createdRow(row, data, index);
      }
    }
  }

  private checkPaging(dataTablesParameters){
    this.markAll = dataTablesParameters.length >= 1000000000;

    if (!dataTablesParameters.length || isNaN(dataTablesParameters.length)){
      dataTablesParameters.length = 1000000000;
    }

    if (!dataTablesParameters.start || isNaN(dataTablesParameters.start)){
      dataTablesParameters.start = 0;
    }

  }

  public preSelectRows(data: any){
    if(!this.deselectedRows.has(JSON.stringify(data))){
      this.selectedRows.add(JSON.stringify(data));
    }
  }

  private updateSelectedRows(data: any){
    const str = JSON.stringify(data);
    
    if(this.dtOptions.select.style == 'single'){
      this.selectedRows.clear();
      this.selectedRows.add(str);
    }

    if(this.dtOptions.select.style == 'multi'){
      if(this.selectedRows.has(str)){
        this.selectedRows.delete(str);
        this.deselectedRows.add(str)
      }else{
        this.selectedRows.add(str);
        this.deselectedRows.delete(str);
      }
    }
  }

  gridAllRowsCheckBoxChecked(e) {
    if (this.gridCheckAll) {
      this.selectedRows.clear();
      this.gridCheckAll = false;
      this.datatableElement.dtInstance.then((dtInstance: DataTables.Api) => {
        dtInstance.rows().every(function (rowIdx, tableLoop, rowLoop) {
          $(dtInstance.row(rowIdx).node()).removeClass('selected')
        });
      });
    }
    else {
      this.selectedRows.clear();
      const parent = this;

      this.datatableElement.dtInstance.then((dtInstance: DataTables.Api) => {
        dtInstance.rows().every(function (rowIdx, tableLoop, rowLoop) {
          $(dtInstance.row(rowIdx).node()).addClass('selected')
          parent.selectedRows.add(JSON.stringify(dtInstance.row(rowIdx).data()))
        });
      });
      this.gridCheckAll = true;
    }
  }
}
